import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FiAlertTriangle, FiCheck, FiX, FiExternalLink, FiDollarSign } from "react-icons/fi";
import libraryService from "../../services/libraryService";

const getStudentName = (student) => {
    if (!student) return "Unknown Student";
    return (
        student.fullName ||
        `${student.firstName || ""} ${student.lastName || ""}`.trim() ||
        "Unknown Student"
    );
};

const STATUS_TABS = [
    { key: "Pending", label: "Pending" },
    { key: "SubmittedForVerification", label: "Awaiting Verification" },
    { key: "Paid", label: "Paid" },
    { key: "Waived", label: "Waived" },
];

const STATUS_BADGE = {
    Pending: <span className="badge" style={{ background: "#fee2e2", color: "#b91c1c" }}>Pending</span>,
    SubmittedForVerification: <span className="badge" style={{ background: "#fef3c7", color: "#b45309" }}>Awaiting Verification</span>,
    Paid: <span className="badge badge-green">Paid</span>,
    Waived: <span className="badge" style={{ background: "#e2e8f0", color: "#475569" }}>Waived</span>,
};

export default function PendingFines() {
    const [tab, setTab] = useState("SubmittedForVerification");
    const [allIssues, setAllIssues] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actingId, setActingId] = useState(null);
    const [rejectTarget, setRejectTarget] = useState(null);
    const [rejectRemarks, setRejectRemarks] = useState("");

    const fetchFines = async () => {
        setLoading(true);
        try {
            // Returned issues carry the fine info; fetch returned records and filter client-side by fine status.
            const res = await libraryService.getIssues({ status: "Returned", limit: 200 });
            const withFines = (res.data.issues || []).filter((i) => i.fineAmount > 0);
            setAllIssues(withFines);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFines();
    }, []);

    const filtered = allIssues.filter((i) => i.finePaymentStatus === tab);

    const handleVerify = async (issue) => {
        setActingId(issue._id);
        try {
            await libraryService.verifyFinePayment(issue._id, "verify");
            toast.success("Payment verified.");
            fetchFines();
        } catch (err) {
            toast.error(err.response?.data?.message || err.message);
        } finally {
            setActingId(null);
        }
    };

    const handleWaive = async (issue) => {
        setActingId(issue._id);
        try {
            await libraryService.verifyFinePayment(issue._id, "waive");
            toast.success("Fine waived.");
            fetchFines();
        } catch (err) {
            toast.error(err.response?.data?.message || err.message);
        } finally {
            setActingId(null);
        }
    };

    const submitReject = async () => {
        if (!rejectTarget) return;
        setActingId(rejectTarget._id);
        try {
            await libraryService.verifyFinePayment(rejectTarget._id, "reject", rejectRemarks);
            toast.success("Payment proof rejected — student can resubmit.");
            setRejectTarget(null);
            setRejectRemarks("");
            fetchFines();
        } catch (err) {
            toast.error(err.response?.data?.message || err.message);
        } finally {
            setActingId(null);
        }
    };

    return (
        <div>
            <div className="page-header">
                <div>
                    <h1 className="page-title">Pending Fines</h1>
                    <p className="page-subtitle">Review late-return fines and verify student payments.</p>
                </div>
            </div>

            <div className="card">
                <div className="card-header" style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
                    {STATUS_TABS.map((t) => (
                        <button
                            key={t.key}
                            type="button"
                            className={`btn btn-sm ${tab === t.key ? "btn-primary" : "btn-outline"}`}
                            onClick={() => setTab(t.key)}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                <div className="card-body" style={{ padding: 0 }}>
                    {loading ? (
                        <div className="loading-center">
                            <div className="spinner" />
                            Loading fines...
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="empty-state">
                            <FiDollarSign />
                            <p>No records in this category.</p>
                        </div>
                    ) : (
                        <div className="table-wrapper">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Student</th>
                                        <th>Book</th>
                                        <th>Returned</th>
                                        <th>Fine</th>
                                        <th>Proof</th>
                                        <th>Status</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map((issue) => (
                                        <tr key={issue._id}>
                                            <td>{getStudentName(issue.student)}</td>
                                            <td>{issue.book?.title || "—"}</td>
                                            <td>{issue.returnDate ? new Date(issue.returnDate).toLocaleDateString() : "—"}</td>
                                            <td style={{ fontWeight: 600 }}>₹{issue.fineAmount}</td>
                                            <td>
                                                {issue.paymentProofUrl ? (
                                                    <a href={issue.paymentProofUrl} target="_blank" rel="noreferrer" className="btn btn-outline btn-sm">
                                                        <FiExternalLink size={12} style={{ marginRight: 4 }} />
                                                        View
                                                    </a>
                                                ) : (
                                                    <span style={{ fontSize: 12, color: "var(--text-muted)" }}>Not submitted</span>
                                                )}
                                            </td>
                                            <td>{STATUS_BADGE[issue.finePaymentStatus] || issue.finePaymentStatus}</td>
                                            <td>
                                                {issue.finePaymentStatus === "SubmittedForVerification" && (
                                                    <div style={{ display: "flex", gap: "0.35rem" }}>
                                                        <button
                                                            className="btn btn-success btn-sm"
                                                            onClick={() => handleVerify(issue)}
                                                            disabled={actingId === issue._id}
                                                            title="Mark as paid"
                                                        >
                                                            <FiCheck size={13} />
                                                        </button>
                                                        <button
                                                            className="btn btn-outline btn-sm"
                                                            onClick={() => setRejectTarget(issue)}
                                                            disabled={actingId === issue._id}
                                                            title="Reject proof"
                                                        >
                                                            <FiX size={13} />
                                                        </button>
                                                    </div>
                                                )}
                                                {issue.finePaymentStatus === "Pending" && (
                                                    <button
                                                        className="btn btn-outline btn-sm"
                                                        onClick={() => handleWaive(issue)}
                                                        disabled={actingId === issue._id}
                                                    >
                                                        Waive
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {rejectTarget && (
                <div className="modal-overlay" onClick={() => setRejectTarget(null)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <h3>
                            <FiAlertTriangle style={{ marginRight: 6, verticalAlign: -2 }} />
                            Reject Payment Proof
                        </h3>
                        <p>Let the student know why — this puts the fine back to "Pending" so they can resubmit.</p>
                        <div className="form-group">
                            <textarea
                                className="form-control"
                                rows={3}
                                placeholder="e.g. Screenshot unclear, please resend"
                                value={rejectRemarks}
                                onChange={(e) => setRejectRemarks(e.target.value)}
                                style={{ resize: "vertical" }}
                            />
                        </div>
                        <div className="modal-actions">
                            <button className="btn btn-outline" onClick={() => setRejectTarget(null)}>
                                Cancel
                            </button>
                            <button className="btn btn-danger" onClick={submitReject} disabled={actingId === rejectTarget._id}>
                                {actingId === rejectTarget._id ? "Rejecting..." : "Reject"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}